import { Injectable } from '@angular/core';
  import { HttpClient, HttpHeaders } from '@angular/common/http';                                                                                  
  import { Observable, Subject, throwError } from 'rxjs';                                                                                        
  import { catchError, map } from 'rxjs/operators';
  import * as signalR from '@microsoft/signalr';
  import { EnvironmentService } from '../environment/environment.service';

  export type SosRole = 'sender' | 'responder';

  export interface SosParticipant {
    participantId: string;
    role: SosRole;
    index?: number;
    colorHex?: string;
    displayName: string;
    lat: number;
    lon: number;
    lastSeenUtc?: string;
    // AC-4683 - sender only, from the app's location pushes; missing from older apps
    battery?: number;
    charging?: boolean;
    intervalSeconds?: number;
    lastMovedUtc?: string;
  }

  export interface SosViewerContext {
    participantId: string;
    role: SosRole;
    displayName: string;
    colorHex?: string;
  }

  export interface SosInitResponse {
    sessionCode: string;
    sessionType?: string;
    status: 'Live' | 'Ended';
    viewer: SosViewerContext;
    participants: SosParticipant[];
    message?: string;
    senderType?: string; // AC-4636 - 'Agent' | 'Consumer'; missing on older API responses = Agent
    endReason?: string;  // AC-4629 - 'Expired' when the phone went silent and the session timed out
    newSessionUrl?: string; // AC-4674 - endReason 'Replaced': this viewer's link into the sender's newer SOS
  }

  export interface SosLocationUpdate {
    sessionCode: string;
    participantId: string;
    role?: string;
    index?: number;
    colorHex?: string;
    lat: number;
    lon: number;
    sentUtc: string;
    battery?: number;
    charging?: boolean;
    intervalSeconds?: number;
    lastMovedUtc?: string;
  }

  /**
   * AC-4683 - the API's database times come without a zone ("2026-11-04T19:24:21.07"), which
   * browsers read as local time; they are UTC.
   */
  export function asUtc(value?: string | null): string | undefined {
    if (!value) return undefined;
    return /(Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : value + 'Z';
  }

  function optNumber(value: any): number | undefined {
    return typeof value === 'number' && isFinite(value) ? value : undefined;
  }

  function optBool(value: any): boolean | undefined {
    return typeof value === 'boolean' ? value : undefined;
  }

  interface SignalRConnectionInfo {
    url: string;
    accessToken: string;
  }

  @Injectable({
    providedIn: 'root'
  })
  export class SosTrackingService {

    private hubConnection?: signalR.HubConnection;
    private readonly updates$ = new Subject<SosLocationUpdate>();
    private readonly trackingEnded$ = new Subject<{ sessionCode: string; message?: string; endReason?: string }>();
    private readonly connectionState$ = new Subject<'reconnecting' | 'reconnected' | 'closed'>();
    private readonly participantJoined$ = new Subject<SosParticipant>();
    private readonly sessionJoined$ = new Subject<string>(); // emits sessionCode once HTTP join completes

    private activeSessionCode = '';
    private participants: SosParticipant[] = [];
    private timer: any;
    private tick = 0;

    constructor(
      private http: HttpClient,
      private environmentService: EnvironmentService
    ) {}

    private getBaseUrl(): string { return this.environmentService.getIA9LightningUrl(); }
    private getKey(): string { return this.environmentService.getIA9LightningKey(); }

    onLocationUpdates(): Observable<SosLocationUpdate> { return this.updates$.asObservable(); }
    onTrackingEnded(): Observable<{ sessionCode: string; message?: string; endReason?: string }> { return this.trackingEnded$.asObservable(); }
    onConnectionStateChange(): Observable<'reconnecting' | 'reconnected' | 'closed'> { return this.connectionState$.asObservable(); }
    onParticipantJoined(): Observable<SosParticipant> { return this.participantJoined$.asObservable(); }
    onSessionJoined(): Observable<string> { return this.sessionJoined$.asObservable(); }
    notifySessionJoined(sessionCode: string): void { this.sessionJoined$.next(sessionCode); }

    initSession(sessionCode: string, participantToken?: string): Observable<SosInitResponse> {
      const url = `${this.getBaseUrl()}agent/sos/session/${encodeURIComponent(sessionCode)}?code=${this.getKey()}`;
      const headersObj: Record<string, string> = {};
      if (participantToken?.trim()) {
        headersObj['X-Participant-Token'] = participantToken.trim();
      }
      return this.http.get<any>(url, { headers: new HttpHeaders(headersObj) }).pipe(
        map((raw: any) => {
          const payload = raw?.data ?? raw?.Data ?? raw;
          const code = payload?.sessionCode ?? payload?.SessionCode ?? sessionCode;
          const status: 'Live' | 'Ended' = this.toUiStatus(payload?.status ?? payload?.Status ?? 'Live');

          const viewerRaw = payload?.viewer ?? payload?.Viewer;
          if (!viewerRaw) throw new Error('SOS init response missing viewer.');

          const viewer: SosViewerContext = {
            participantId: viewerRaw.participantId ?? viewerRaw.ParticipantId ?? '',
            role: (viewerRaw.role ?? viewerRaw.Role) as SosRole,
            displayName: viewerRaw.displayName ?? viewerRaw.DisplayName ?? 'Viewer',
            colorHex: viewerRaw.colorHex ?? viewerRaw.ColorHex
          };
          if (!viewer.participantId) throw new Error('SOS init response missing viewer.participantId.');

          const listRaw = payload?.participants ?? payload?.Participants ?? [];
          const participants: SosParticipant[] = (Array.isArray(listRaw) ? listRaw : []).map((x: any) => ({
            participantId: String(x.participantId ?? x.ParticipantId ?? '').trim(),
            role: (x.role ?? x.Role) as SosRole,
            index: x.index ?? x.Index ?? undefined,
            colorHex: x.colorHex ?? x.ColorHex ?? undefined,
            displayName: x.displayName ?? x.DisplayName ?? (x.role === 'sender' ? 'Sender' : 'Responder'),
            lat: Number(x.lat ?? x.Lat ?? 0),
            lon: Number(x.lon ?? x.Lon ?? 0),
            lastSeenUtc: asUtc(x.lastSeenUtc ?? x.LastSeenUtc),
            battery: optNumber(x.battery),
            charging: optBool(x.charging),
            intervalSeconds: optNumber(x.intervalSeconds),
            lastMovedUtc: asUtc(x.lastMovedUtc)
          }));

          return { sessionCode: code, sessionType: payload?.sessionType, status, viewer, participants, message: payload?.message, senderType: payload?.senderType ?? payload?.SenderType,
            endReason: payload?.endReason ?? payload?.EndReason,
            newSessionUrl: payload?.newSessionUrl ?? payload?.NewSessionUrl } as
  SosInitResponse;
        }),
        catchError(err => throwError(err))
      );
    }

    joinSession(sessionCode: string, participantToken: string, connectionId: string): Observable<any> {
      const url = `${this.getBaseUrl()}agent/sos/session/${encodeURIComponent(sessionCode)}/join?code=${this.getKey()}`;
      return this.http.post(url, { participantToken, connectionId }).pipe(
        catchError(err => throwError(err))
      );
    }

    pushParticipantLocation(sessionCode: string, participantToken: string, lat: number, lon: number): Observable<any> {
      const url = `${this.getBaseUrl()}agent/sos/session/${encodeURIComponent(sessionCode)}/location?code=${this.getKey()}`;
      return this.http.post(url, { participantToken, lat, lon }).pipe(
        catchError(err => throwError(err))
      );
    }

    markSafe(sessionCode: string, participantToken: string): Observable<any> {
      const url = `${this.getBaseUrl()}agent/sos/session/${encodeURIComponent(sessionCode)}/safe?code=${this.getKey()}`;
      return this.http.post(url, { participantToken }).pipe(
        catchError(err => throwError(err))
      );
    }

    // AC-4621 - this page has no member sign-in, so cancel proves the sender with the
    // session's participant token (agent/sos/cancel needs a bearer token and answered 401).
    cancelSos(sessionCode: string, participantToken: string): Observable<any> {
      const url = `${this.getBaseUrl()}agent/sos/session/${encodeURIComponent(sessionCode)}/cancel?code=${this.getKey()}`;
      return this.http.post(url, { participantToken }).pipe(
        catchError(err => throwError(err))
      );
    }

    startSignalR(sessionCode: string, participantToken: string, isReconnect = false): void {
      this.stopSignalR();
      const negotiateUrl = `${this.getBaseUrl()}agent/sos/negotiate?code=${this.getKey()}&sessionCode=${encodeURIComponent(sessionCode)}`;

      this.http.get<any>(negotiateUrl).subscribe({
        next: (raw) => {
          const connInfo = this.normalizeConnInfo(raw);
          if (!connInfo.url || !connInfo.accessToken) {
            console.error('[SOS] negotiate response missing url/accessToken:', raw);
            return;
          }

          this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(connInfo.url, {
              accessTokenFactory: () => connInfo.accessToken
              // skipNegotiation: true,
              // transport: signalR.HttpTransportType.WebSockets
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

          this.hubConnection.onreconnecting(() => this.connectionState$.next('reconnecting'));
          this.hubConnection.onreconnected(() => this.connectionState$.next('reconnected'));
          this.hubConnection.onclose(() => this.connectionState$.next('closed'));

          this.hubConnection.on('OnLocationUpdated', (payload: any) => {
            this.updates$.next({
              sessionCode: payload.sessionCode ?? sessionCode,
              participantId: String(payload.participantToken ?? payload.participantId ?? '').trim(),
              role: payload.role,
              index: payload.index,
              colorHex: payload.colorHex ?? payload.ColorHex,
              lat: Number(payload.lat),
              lon: Number(payload.lon),
              sentUtc: asUtc(payload.sentUtc) ?? new Date().toISOString(),
              battery: optNumber(payload.battery),
              charging: optBool(payload.charging),
              intervalSeconds: optNumber(payload.intervalSeconds),
              lastMovedUtc: asUtc(payload.lastMovedUtc)
            });
          });

          this.hubConnection.on('OnSessionEnded', (payload: any) => {
            console.log('[SOS] OnSessionEnded:', payload);
            this.trackingEnded$.next({
              sessionCode: String(payload?.sessionCode ?? sessionCode).trim(),
              message: String(payload?.message ?? '').trim(),
              endReason: payload?.endReason ?? payload?.EndReason
            });
          });

          this.hubConnection.on('OnParticipantJoined', (payload: any) => {
            const x = payload?.participant ?? payload?.Participant;
            if (!x) return;
            this.participantJoined$.next({
              participantId: String(x.participantId ?? x.ParticipantId ?? '').trim(),
              role: (x.role ?? x.Role) as SosRole,
              index: x.index ?? x.Index ?? undefined,
              colorHex: x.colorHex ?? x.ColorHex ?? undefined,
              displayName: x.displayName ?? x.DisplayName ?? 'Responder',
              lat: Number(x.lat ?? x.Lat ?? 0),
              lon: Number(x.lon ?? x.Lon ?? 0),
              lastSeenUtc: asUtc(x.lastSeenUtc ?? x.LastSeenUtc)
            });
          });

          this.hubConnection.start()
            .then(() => {
              console.log('[SOS] SignalR started, connectionId:', this.hubConnection?.connectionId);
              // Server uses UserId = sessionCode routing (set at negotiate time),
              // so all participants already receive broadcasts — no hub group join needed.
              if (isReconnect) {
                this.connectionState$.next('reconnected');
              }
              // HTTP join is fired independently from the component — see startSignalR caller.
              // Keeping the chain clean so a WebSocket failure never blocks the ack SMS.
            })
            .catch(err => console.error('[SOS] SignalR start error:', err));
        },
        error: (err) => console.error('[SOS] negotiate error:', err)
      });
    }

    stopSignalR(): void {
      if (!this.hubConnection) return;
      const c = this.hubConnection;
      this.hubConnection = undefined;
      c.stop().catch(err => console.error('[SOS] stop error:', err));
    }

    initMockSession(sessionCode: string, participantToken?: string): SosInitResponse {
      this.activeSessionCode = sessionCode;
      const viewerIsResponder = !!(participantToken?.trim());
      const senderId = 'aaaa-0000-sender';
      const resp1Id = 'bbbb-1111-resp1';
      const resp2Id = 'cccc-2222-resp2';
      const baseLat = 37.7749, baseLon = -122.4194;

      const participants: SosParticipant[] = [
        { participantId: senderId, role: 'sender', colorHex: '#E53935', displayName: 'Sender', lat: baseLat, lon: baseLon },
        { participantId: resp1Id, role: 'responder', index: 1, colorHex: '#1E88E5', displayName: 'Sam', lat: baseLat + 0.004, lon: baseLon + 0.002
  },
        { participantId: resp2Id, role: 'responder', index: 2, colorHex: '#43A047', displayName: 'Tim', lat: baseLat - 0.003, lon: baseLon - 0.0015
   }
      ];
      this.participants = participants;

      const viewer: SosViewerContext = viewerIsResponder
        ? { participantId: resp1Id, role: 'responder', displayName: 'Sam', colorHex: '#1E88E5' }
        : { participantId: senderId, role: 'sender', displayName: 'Me', colorHex: '#E53935' };

      return { sessionCode, status: 'Live', viewer, participants, message: 'Mock session' };
    }

    startMockLiveUpdates(): void {
      this.stopMockLiveUpdates();
      if (!this.activeSessionCode || !this.participants.length) return;
      this.tick = 0;
      this.timer = setInterval(() => {
        this.tick++;
        this.participants = this.participants.map((p, i) => {
          const phase = (this.tick / 10) + i;
          const lat = p.lat + 0.0022 * Math.sin(phase) * 0.15;
          const lon = p.lon + 0.0022 * Math.cos(phase) * 0.15;
          const sentUtc = new Date().toISOString();
          this.updates$.next({ sessionCode: this.activeSessionCode, participantId: p.participantId, lat, lon, sentUtc });
          return { ...p, lat, lon, lastSeenUtc: sentUtc };
        });
      }, 2000);
    }

    stopMockLiveUpdates(): void {
      if (this.timer) { clearInterval(this.timer); this.timer = undefined; }
    }

    private normalizeConnInfo(raw: any): SignalRConnectionInfo {
      return {
        url: raw?.url || raw?.Url || raw?.clientEndpoint || '',
        accessToken: raw?.accessToken || raw?.AccessToken || ''
      };
    }

    private toUiStatus(s: string): 'Live' | 'Ended' {
      const v = String(s || '').toLowerCase();
      return (v === 'ended' || v === 'completed' || v === 'closed') ? 'Ended' : 'Live';
    }

    distanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
      const R = 3958.8;
      const toRad = (x: number) => (x * Math.PI) / 180;
      const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
  }